import React, { useEffect, useRef } from 'react';
import './UserProfileCard.css';
import { initStarsParallax, destroyStarsParallax } from '../../utils/starsParallax';

const UserProfileCard = ({ user, onClose, onDM, onBlock, currentUser }) => {
  const canModerate = currentUser.isAdmin || ['admin', 'moderator'].includes(currentUser.accountType);
  const isSelf = user._id === currentUser._id;
  
  // Ref for card to attach parallax animation
  const cardRef = useRef(null);
  const parallaxInstanceRef = useRef(null);

  // Cosmetic helper functions
  const getAvatarFrameClass = () => {
    if (!user.equippedCosmetics?.avatarFrame) return '';
    const frameId = user.equippedCosmetics.avatarFrame;
    const frameMap = {
      'frame-neon-purple': 'neon-purple-frame',
      'frame-gold-elite': 'gold-elite-frame',
      'frame-rgb-animated': 'rgb-animated-frame',
      'frame-fire': 'fire-frame',
      'frame-ice': 'ice-frame',
      'frame-electric-storm': 'electric-storm-frame',
      'frame-dragon-scale': 'dragon-scale-frame',
      'frame-cosmic-orbit': 'cosmic-orbit-frame',
      'frame-shadow-realm': 'shadow-realm-frame',
      'frame-cherry-blossom': 'cherry-blossom-frame',
      'frame-toxic-glow': 'toxic-glow-frame',
      'frame-holographic': 'holographic-frame',
      'frame-lava-flow': 'lava-flow-frame',
      'frame-digital-glitch': 'digital-glitch-frame',
      'frame-celestial': 'celestial-frame'
    };
    return frameMap[frameId] || '';
  };

  const getUsernameEffectClass = () => {
    if (!user.equippedCosmetics?.usernameEffect) return '';
    // Return the ID directly as it matches the CSS class name
    return user.equippedCosmetics.usernameEffect;
  };

  const getBadgeIcons = () => {
    if (!user.equippedCosmetics?.badges || user.equippedCosmetics.badges.length === 0) return null;
    const badgeMap = {
      'badge-og-member': '🏆',
      'badge-giveaway-legend': '🎁',
      'badge-social-star': '⭐',
      'badge-collector': '💎',
      'badge-game-master': '🎮',
      'badge-speedrunner': '🏃',
      'badge-night-owl': '🦉',
      'badge-achievement-hunter': '🎯',
      'badge-community-hero': '🦸',
      'badge-code-breaker': '🔐',
      'badge-strategist': '🧠',
      'badge-completionist': '📋',
      'badge-mvp': '👑',
      'badge-digital-artist': '🎨',
      'badge-beta-tester': '🔬'
    };
    return user.equippedCosmetics.badges.map(badgeId => badgeMap[badgeId] || '').filter(Boolean);
  };

  const getCardBackgroundClass = () => {
    if (!user.equippedCosmetics?.cardBackground) return '';
    // The ID from the database already matches the CSS class name (e.g., 'card-bg-gradient-sunset')
    return user.equippedCosmetics.cardBackground;
  };

  const getCardBorderClass = () => {
    if (!user.equippedCosmetics?.cardBorder) return '';
    // The ID from the database already matches the CSS class name (e.g., 'card-border-elegant-gold')
    return user.equippedCosmetics.cardBorder;
  };

  // Initialize Stars Parallax background when card-bg-stars-parallax is equipped
  useEffect(() => {
    if (cardRef.current && user.equippedCosmetics?.cardBackground === 'card-bg-stars-parallax') {
      // Small delay to ensure the card is fully rendered
      const timer = setTimeout(() => {
        if (cardRef.current && !parallaxInstanceRef.current) {
          parallaxInstanceRef.current = initStarsParallax(cardRef.current);
        }
      }, 100);
      
      return () => {
        clearTimeout(timer);
        if (parallaxInstanceRef.current) {
          destroyStarsParallax(parallaxInstanceRef.current);
          parallaxInstanceRef.current = null;
        }
      };
    } else {
      // Clean up if background changes
      if (parallaxInstanceRef.current) {
        destroyStarsParallax(parallaxInstanceRef.current);
        parallaxInstanceRef.current = null;
      }
    }
  }, [user.equippedCosmetics?.cardBackground]);


  const handleBlock = async () => {
    if (!window.confirm(`Block ${user.username}?`)) return;

    try {
      const response = await fetch(`/api/chat/rooms/block/${user._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ reason: 'Blocked from chat' })
      });

      if (response.ok) {
        alert('User blocked successfully');
        onClose();
      }
    } catch (error) {
      console.error('Failed to block user:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      online: '#10b981',
      away: '#f59e0b',
      busy: '#ef4444',
      dnd: '#dc2626',
      offline: '#6b7280'
    };
    return colors[status] || colors.offline;
  };

  const getStatusText = (status) => {
    const texts = {
      online: 'Online',
      away: 'Away',
      busy: 'Busy',
      dnd: 'Do Not Disturb',
      offline: 'Offline'
    };
    return texts[status] || 'Unknown';
  };

  return (
    <div className="profile-card-overlay" onClick={onClose}>
      <div ref={cardRef} className={`profile-card ${getCardBackgroundClass()} ${getCardBorderClass()}`} onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>

        <div className="profile-header">
          {user.profile?.avatar?.url ? (
            <img 
              src={user.profile.avatar.url} 
              alt={user.username}
              className={`profile-avatar ${getAvatarFrameClass()}`}
            />
          ) : (
            <div className={`profile-avatar avatar-placeholder ${getAvatarFrameClass()}`}>
              {user.username[0].toUpperCase()}
            </div>
          )}
          <div className="profile-info">
            <h3 className={`profile-username ${getUsernameEffectClass()}`}>
              {user.username}
              {getBadgeIcons() && (
                <span className="cosmetic-badges-chat">
                  {getBadgeIcons().map((icon, idx) => (
                    <span key={idx} className="cosmetic-badge-inline">{icon}</span>
                  ))}
                </span>
              )}
              {user.accountType === 'admin' && <span className="badge badge-admin">ADMIN</span>}
              {user.accountType === 'moderator' && <span className="badge badge-mod">MOD</span>}
            </h3>
            
            {user.chatStatus && (
              <div className="profile-status">
                <span 
                  className="status-dot"
                  style={{ backgroundColor: getStatusColor(user.chatStatus) }}
                />
                <span className="status-text">{getStatusText(user.chatStatus)}</span>
              </div>
            )}

            {user.customStatus && (
              <div className="custom-status">
                {user.customStatus}
              </div>
            )}

            {user.lastSeen && user.chatStatus === 'offline' && (
              <div className="last-seen">
                Last seen {new Date(user.lastSeen).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {user.profile?.bio && (
          <div className="profile-bio">
            <h4>Bio</h4>
            <p>{user.profile.bio}</p>
          </div>
        )}

        <div className="profile-stats">
          <div className="stat">
            <strong>{user.followerCount || user.followers?.length || 0}</strong>
            <span>Followers</span>
          </div>
          <div className="stat">
            <strong>{user.followingCount || user.following?.length || 0}</strong>
            <span>Following</span>
          </div>
          <div className="stat">
            <strong>{user.activityPoints?.available || user.activityPoints?.current || 0}</strong>
            <span>Points</span>
          </div>
        </div>

        {!isSelf && (
          <div className="profile-actions">
            <button className="action-btn primary" onClick={() => {
              onDM(user);
              onClose();
            }}>
              💬 Send Message
            </button>
            
            <button className="action-btn" onClick={() => {
              window.location.href = `/profile/${user.username}`;
            }}>
              👤 View Profile
            </button>

            <button className="action-btn danger" onClick={handleBlock}>
              🚫 Block User
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileCard;
